/**
 * Zod schema for the cohort forms — replaces `cohort-validation.ts`.
 *
 * The rules and their reasoning are carried over unchanged; what changes is
 * how a message reaches a field. The old module returned `string[]` and the
 * pages worked out where each belonged by comparing against exported message
 * constants (`blockingErrors.includes(START_REQUIRED_ERROR)`). Identity
 * matching on user-facing prose means editing a sentence silently detaches its
 * error from its field. Zod issues carry a `path`, so the association is
 * declared once, here.
 *
 * The server is still the authority and re-checks everything — a DB CHECK for
 * the window ordering, `admin/cohorts/dto.check_window_and_send` for the rest,
 * re-run against the MERGED row on update. This exists so an operator finds out
 * while looking at the form instead of after a save.
 *
 * **Only rules the client can evaluate completely**, same line as before.
 * Anything needing server state — a name already taken in this client, whether
 * the client is internal, whether the cohort still has customer members —
 * stays a server error surfaced through `messageForCohortError`. Half-checking
 * those here would produce a form that passes and a save that fails.
 */
import { z } from 'zod'
import { tooLong } from '../common/length-message'
import type { SendMode } from './cohort-form-fields'

/**
 * The ISO date a send resolves to, or null when nothing is scheduled.
 *
 * Mirrors `provisioning.scheduling.resolve_invite_send_at`. Still a plain
 * function rather than part of the schema because the FIELDS render it too —
 * "this lands on 2026-08-25" is shown while typing — and the fields and the
 * submit gate must not disagree about what an offset means.
 */
export function resolveSendDay(
  mode: SendMode,
  fixedAt: string,
  offsetDays: string,
  startDate?: string,
): string | null {
  if (mode === 'fixed') return fixedAt === '' ? null : fixedAt.slice(0, 10)
  if (mode !== 'offset' || !startDate) return null
  const offset = Number(offsetDays)
  if (!Number.isFinite(offset) || offsetDays === '') return null
  const day = new Date(`${startDate}T00:00:00Z`)
  day.setUTCDate(day.getUTCDate() + offset)
  return day.toISOString().slice(0, 10)
}

const base = z.object({
  name: z.string().trim().min(1, 'A cohort name is required.').max(200, tooLong(200)),
  description: z.string().max(2000, tooLong(2000)).optional(),
  /** ISO date, or '' for a legacy/edit-time immediate start. */
  scheduledStartOn: z.string(),
  accessEndsOn: z.string(),
  defaultTier: z.enum(['essential', 'pro', 'premium', 'unlimited']),
  defaultProgramId: z
    .string()
    .trim()
    .pipe(z.guid('Choose a program for future enrollments.')),
  defaultTrophyGoal: z.string(),
  externalPolicyRef: z.string().max(500, tooLong(500)),
  isTrial: z.boolean(),
  /** Three-state: null = inherit the client's classification. */
  isInternal: z.boolean().nullable(),
  hrUserIds: z.array(z.string()),
  sendMode: z.enum(['none', 'fixed', 'offset']),
  /** `datetime-local` value, or ''. */
  sendFixedAt: z.string(),
  /** A STRING so an in-progress '-' is not coerced to 0. */
  sendOffsetDays: z.string(),
})

/**
 * One schema for create AND edit.
 *
 * There is no `requireStartDate` switch. A start date is required, full stop,
 * because nothing can produce a cohort without one: `CreateCohortBody.
 * scheduled_start_on` is a bare `date`, and the seed always sets one. The
 * branch that used to make it optional on edit was justified as supporting
 * cohorts that "predate the requirement" — data that does not exist in any
 * environment, and cannot, since there is no production yet and staging is
 * synthetic.
 *
 * It was also a HOLE rather than mere dead weight: the edit form sent `null`
 * for an emptied field, so it could manufacture the very state create forbids.
 */
export function cohortSchema() {
  return base.superRefine((state, ctx) => {
    const trophyGoal = Number(state.defaultTrophyGoal)
    if (
      state.defaultTrophyGoal.trim() === '' ||
      !Number.isInteger(trophyGoal) ||
      trophyGoal <= 0
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['defaultTrophyGoal'],
        message:
          'Set a positive whole-number trophy goal confirmed for this cohort.',
      })
    }

    if (state.scheduledStartOn === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['scheduledStartOn'],
        message:
          'Set a start date. A new cohort needs a fixed access window; use individual student overrides for exceptions.',
      })
    }

    if (state.accessEndsOn === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['accessEndsOn'],
        message:
          'Set an end date. For open-ended access, choose a far-future date deliberately.',
      })
    }

    if (
      state.scheduledStartOn !== '' &&
      state.accessEndsOn !== '' &&
      state.accessEndsOn < state.scheduledStartOn
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['accessEndsOn'],
        message:
          'The end date is before the start date — the cohort would end before it begins.',
      })
    }

    // An offset with nothing to be relative to resolves to NO send at all, so
    // the whole wave silently never goes out. Both pages also switch the radio
    // off when the start date is cleared; this covers the reverse order — pick
    // the offset first, never set a start.
    if (state.sendMode === 'offset' && state.scheduledStartOn === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['sendOffsetDays'],
        message:
          'A relative send date needs a start date to be relative to. Set a start date, or switch to a fixed send date.',
      })
    }

    // MIRRORS the server's `OffsetDays` ceiling (le=0, `admin/cohorts/
    // dto.py`) — change the two together. A relative send exists to LEAD
    // the start; inviting people after it (a straggler wave) is the fixed
    // date's job, and the message says so.
    if (
      state.sendMode === 'offset' &&
      state.sendOffsetDays !== '' &&
      Number(state.sendOffsetDays) > 0
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['sendOffsetDays'],
        message:
          'A relative send goes out on or before the start date (0 sends on the start day). To invite people after the start, use a fixed send date.',
      })
    }

    if (state.sendMode === 'fixed' && state.sendFixedAt === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['sendFixedAt'],
        message: 'Set the send date and time, or choose "Nothing scheduled".',
      })
    }

    // A send BEFORE the start is the normal case, and a send AFTER the start is
    // legitimate (a straggler joining an in-flight cohort). A send after access
    // has ENDED is never coherent — the invite is dead on arrival.
    const sendDay = resolveSendDay(
      state.sendMode,
      state.sendFixedAt,
      state.sendOffsetDays,
      state.scheduledStartOn,
    )
    if (
      sendDay !== null &&
      state.accessEndsOn !== '' &&
      sendDay > state.accessEndsOn
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['sendFixedAt'],
        message: `The invite would go out on ${sendDay}, after access ends (${state.accessEndsOn}), so it would arrive as a dead link.`,
      })
    }
  })
}

export type CohortForm = z.infer<typeof base>
